'use client';

import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type FocusTrapOptions = {
  active: boolean;
  onEscape?: () => void;
  /** Where focus returns when the trap deactivates. Falls back to the element focused at activation. */
  returnFocusRef?: RefObject<HTMLElement | null>;
};

/**
 * Traps Tab focus inside a container while active (an open overlay), closes on
 * Escape, and restores focus when deactivated. The trap exists only while the
 * overlay is open — pinned, non-modal surfaces must not use it.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  { active, onEscape, returnFocusRef }: FocusTrapOptions
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Captured now (activation time) rather than read from the ref at cleanup
    // time, since the trigger button rendered outside the trap is already
    // mounted by the time the trap activates and does not change while it's open.
    const explicitReturnTarget = returnFocusRef?.current ?? null;

    // Move focus into the overlay so keyboard users start inside it.
    const initial = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (initial ?? container).focus({ preventScroll: true });

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((element) => !element.hasAttribute('hidden'));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const target = explicitReturnTarget ?? previouslyFocused;
      if (target?.isConnected) target.focus({ preventScroll: true });
    };
  }, [active, containerRef, onEscape, returnFocusRef]);
}
